import { Injectable, OnDestroy } from '@angular/core';
import {
  AuthenticatedResult,
  EventTypes,
  OidcSecurityService,
  PublicEventsService
} from 'angular-auth-oidc-client';
import {
  BehaviorSubject,
  Observable,
  Subject,
  defer,
  filter,
  fromEvent,
  retry,
  switchMap,
  take,
  zip,
  interval,
  of
} from 'rxjs';
import { GlobalCommonFacadeService } from './facades/global-common-facade.service';
import { ApplicationInsightsService } from './application-insights.service';
import { UserService } from './user.service';
import { DEFAULT_OAUTH_CONFIG_ID, STAFF_OAUTH_CONFIG_ID } from './app-config.service';
import { catchError, takeUntil, tap} from 'rxjs/operators';
import { UserActivityService } from './user-activity.service';


export enum AuthType {
  Default = 'default',
  Staff = 'staff'
}


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
  readonly MAX_TRIES = 3;
  readonly REDIRECT_URL_PROPERTY = 'redirectUrl';
  readonly SMART_SILENT_RENEW_INTERVAL = 10 * 60 * 1000; // 10 minutes
  readonly SMART_SILENT_RENEW_INACTIVITY_THRESHOLD = 15; // minutes

  private _activeAuthType$ = new BehaviorSubject<AuthType>(AuthType.Default);

  get activeAuthType$(): Observable<AuthType> {
    return this._activeAuthType$.asObservable();
  }

  get activeAuthType(): AuthType {
    return this._activeAuthType$.value;
  }

  // Returns active auth config id
  get activeConfigId(): string {
    return this.activeAuthType === AuthType.Staff
      ? STAFF_OAUTH_CONFIG_ID
      : DEFAULT_OAUTH_CONFIG_ID;
  }

  // Flag indicates if listeners have been registered
  private listenersRegistered = false;


  private _authenticated$ = new BehaviorSubject<boolean>(false);
  private _tries = 0;
  private _token$ = new BehaviorSubject<string>(null);
  private _allowedFeatures$ = new BehaviorSubject<Array<string>>([]);
  private _isInitializing$ = new BehaviorSubject<boolean>(false);
  private _postLoginUrl = new BehaviorSubject<string>(null);
  private _isTokenClaimsSet = false;
  private _oauthError = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();

  localStorageEvents$ = fromEvent<StorageEvent>(window, 'storage');

  // defer to prevent calling checkAuth on init
  private checkAuth$(configId: string) {
    return defer(() => this.oidcSecurityService.checkAuth(window.location.href, configId));
  }

  get isTokenClaimsSet(): boolean {
    return this._isTokenClaimsSet;
  }

  get authenticated$(): Observable<boolean> {
    return this._authenticated$.asObservable();
  }

  get token$(): Observable<string> {
    return this._token$.asObservable().pipe(filter((token) => !!token));
  }

  get allowedFeatures$(): Observable<Array<string>> {
    return this._allowedFeatures$.asObservable();
  }

  get isInitializing$(): Observable<boolean> {
    return this._isInitializing$.asObservable();
  }

  get postLoginUrl$(): Observable<string> {
    return this._postLoginUrl.asObservable();
  }

  get oauthError$(): Observable<boolean> {
    return this._oauthError.asObservable();
  }

  updateAuthenticated(isAuthenticated: boolean) {
    this._authenticated$.next(isAuthenticated);
  }

  updateAllowedFeatures(allowedFeatures: Array<string>) {
    this._allowedFeatures$.next(allowedFeatures);
    this._isInitializing$.next(false);
  }

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private eventService: PublicEventsService,
    private globalCommonFacadeService: GlobalCommonFacadeService,
    private appInsightService: ApplicationInsightsService,
    private userService: UserService,
    private userActivity: UserActivityService
  ) {}

  init(authType: AuthType = AuthType.Default) {
    console.log('[AuthenticationService] init', authType);
    this._activeAuthType$.next(authType);
    const configId = this.activeConfigId;

    if (!this.listenersRegistered) {
      this.registerOauthStateRedirectUrl();
      this.registerTokenAndClaims();
      this.registerLogoutListener();
      this.registerTokenRefreshListener();
      this.registerSuccessfulLoginListener();
      this.registerOAuthErrorsListener();
      this.registerTokenExpiredHandler();
      this.registerSilentRenewFailure();
      this.setupSmartSilentRenewAfterLogin();
      this.listenersRegistered = true;
    } else {
      console.warn('[AuthenticationService] Listeners already registered, skipping init.');
    }

    // check if it is a redirect from b2c login page after sign up
    const signUpFlow = window.location.href.includes('#id_token');
    if (signUpFlow) {
      this.oidcSecurityService.setState(this.getCurrentRoute(), configId).subscribe((v)=>{
        // TODO: it doesn't cause automatic sing-in after sign-up (only redirects to the b2c sing-in page - needs to be improved with Angular 16 upgrade
        this.oidcSecurityService.authorize(configId);
      });
    } else {
      this.initAuthorization(configId);
    }
  }

  clearPostLoginUrl() {
    // used after the user is redirected to it from the b2c login page
    this._postLoginUrl.next(null);
  }

  logout(configId = this.activeConfigId) {
    // user is redirected to the b2c logout page (postLogoutRedirectUri in the oauth config)
    this.oidcSecurityService.logoff(configId).subscribe();
  }

  private initAuthorization(configId: string) {
    console.log('[Auth] initAuthorization configId:', configId);
    this._isInitializing$.next(true);

    const currentUrl = window.location.href;
    const parsedUrl = new URL(currentUrl);
    const isAuthCallback = parsedUrl.searchParams.has('code') || parsedUrl.searchParams.has('state');

    this.checkAuth$(configId)
      .pipe(retry(2))
      .subscribe({
        next: ({ isAuthenticated, accessToken, errorMessage }) => {
          console.log('[Auth] checkAuth result', {
            configId,
            isAuthenticated,
            hasAccessToken: !!accessToken,
            errorMessage,
            tries: this._tries,
          });

          if (!isAuthenticated && !errorMessage && this._tries < this.MAX_TRIES) {
            if (!isAuthCallback) {
              console.log('[Auth] calling authorize for', configId);
              this.setOauthState(configId);
              this.oidcSecurityService.authorize(configId);
              this._tries++;
            }
          }

          if (isAuthenticated && accessToken) {
            console.log('[Auth] user is authenticated, accessToken received for', configId);
            this._authenticated$.next(true);
            this._token$.next(accessToken);
          }
          this._isInitializing$.next(false);
        },
        error: (error) => {
          // only after trying to check auth 3 times
          console.error('[AuthenticationService] Error checking auth', {
            configId,
            error,
            tries: this._tries,
          });
          this._isInitializing$.next(false);
        }
      });
  }

  private registerLogoutListener() {
    // listens to the localstorage event and logs out the user if the authnResult is not present
    // needed if the user logs out from another tab
    this.localStorageEvents$
      .pipe(
        filter(
          (e) =>
            (e.key === DEFAULT_OAUTH_CONFIG_ID || e.key === STAFF_OAUTH_CONFIG_ID) &&
            e.newValue !== null &&
            JSON.parse(e.newValue)['authnResult'] === undefined
        )
      )
      .subscribe((e) => {
        const configId =
          e.key === STAFF_OAUTH_CONFIG_ID ? STAFF_OAUTH_CONFIG_ID : DEFAULT_OAUTH_CONFIG_ID;
        this.logout(configId);
      });
  }

  private registerTokenAndClaims() {
    this.oidcSecurityService.isAuthenticated$
      .pipe(
        tap((authState) => {
          console.log('[AuthenticationService] isAuthenticated', authState);
        }),
        filter((authState: AuthenticatedResult) => {
          const current = authState?.allConfigsAuthenticated?.find((c) => c.configId === this.activeConfigId);
          console.log('[AuthenticationService] registerTokenAndClaimsLogic - isAuthenticated for', this.activeConfigId, current);
          return !!current?.isAuthenticated;
        }),
        switchMap(() => {
          return this.oidcSecurityService
            .getPayloadFromAccessToken(undefined, this.activeConfigId)
            .pipe(filter((payload) => payload !== null));
        })
      )
      .subscribe({
        next: (payload) => {
          console.log('[AuthenticationService] Token claims received', payload);
          if (Object.keys(payload).length > 0) {
            console.log(
              '[AuthenticationService] Updating token claims in GlobalCommonFacadeService and ApplicationInsightsService'
            );
            this.globalCommonFacadeService.updateTokenClaims(payload);
            this.appInsightService.setClaimsData(payload);
            this._isTokenClaimsSet = true;
          }
        },
        error: (error) => {
          console.error('[AuthenticationService] Error getting token claims', error);
        },
      });
  }

  private registerOauthStateRedirectUrl() {
    // this will update token in service when silent refresh regenerate token
    const newAuthResult$ = this.eventService.registerForEvents().pipe(
      filter((notification) => notification.type === EventTypes.NewAuthenticationResult),
      tap((notification) => console.log('[NewAuthenticationResult]', notification))
    );

    // zip([newAuthResult$, this.oidcSecurityService.getState(this.activeConfigId)])
    //   .pipe(filter(([v, state]) => !!v && !!state))
    //   .subscribe(([v, state]) => {
    //     if (v.value?.isRenewProcess === false) {
    //       this._postLoginUrl.next(window.atob(state)); // set custom url after b2c login redirect only when it is not silent refresh
    //     }
    //   });

    newAuthResult$
      .pipe(
        switchMap(() =>
          zip([
            this.oidcSecurityService.getState(DEFAULT_OAUTH_CONFIG_ID),
            this.oidcSecurityService.getState(STAFF_OAUTH_CONFIG_ID),
          ])
        )
      )
      .subscribe(([defaultState, staffState]) => {
        const state = staffState || defaultState;
        if (!state) {
          return;
        }

        try {
          this._postLoginUrl.next(window.atob(state));
        } catch (err) {
          console.warn('[AuthenticationService] Failed to decode post login state', err);
        }
      });
  }

  private getCurrentRoute(): string {
    return window.btoa(
      window.location.pathname + window.location.search + window.location.hash
    );
  }

  private setOauthState(configId: string) {
    console.log('[Auth] setOauthState - setting state for configId:', configId, 'current url:', this.getCurrentRoute());
    this.oidcSecurityService
      .setState(this.getCurrentRoute(), configId)
      .pipe(take(1))
      .subscribe();
  }

  private registerTokenRefreshListener() {
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) =>
            notification.type === EventTypes.NewAuthenticationResult &&
            notification.value.isRenewProcess === true &&
            notification.value.isAuthenticated === true
        ),
        switchMap(() =>
          zip([
            this.oidcSecurityService.getAccessToken(DEFAULT_OAUTH_CONFIG_ID),
            this.oidcSecurityService.getAccessToken(STAFF_OAUTH_CONFIG_ID),
          ])
        )
      )
      .subscribe(([defaultToken, staffToken]) => {
        const token = staffToken || defaultToken;
        if (token) {
          this._token$.next(token);
        }
      });
  }

  private registerSuccessfulLoginListener() {
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) =>
            notification.type === EventTypes.NewAuthenticationResult &&
            notification.value.isRenewProcess === false &&
            notification.value.isAuthenticated === true
        ),
        switchMap(() => this.userService.logSuccessfulLogin()),
        catchError((error) => {
          console.error('[AuthenticationService] Failed to log successful login', error);
          return of(void 0);
        })
      )
      .subscribe();
  }

  private registerOAuthErrorsListener() {
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) =>
            notification.type === EventTypes.CheckingAuthFinishedWithError ||
            notification.type === EventTypes.ConfigLoadingFailed
        )
      )
      .subscribe((notification) => {
        console.error('[AuthenticationService] OAuth error', notification);
        this._oauthError.next(true);
        this.redirectToUnauthorizedHtml();
      });
  }

  private registerTokenExpiredHandler() {
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) =>
            notification.type === EventTypes.TokenExpired
        )
      )
      .subscribe((notification) => {
        console.error('[AuthenticationService] Token expired', notification);
        this.logout();
      })
  }

  private registerSilentRenewFailure() {
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) =>
            notification.type === EventTypes.SilentRenewFailed
        )
      )
      .subscribe((notification) => {
        console.error('[AuthenticationService] Silent renew failed', notification);
      });
  }

  private setupSmartSilentRenewAfterLogin(): void {
    console.debug('[AuthenticationService] setupSmartSilentRenewAfterLogin')
    this.oidcSecurityService.isAuthenticated$
      .pipe(
        filter(({ isAuthenticated }) => isAuthenticated),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.startSmartSilentRenew();
      });
  }

  /**
   * Starts a loop that checks for user activity and refreshes the token if the user was active recently.
   * Loop is canceled on service destroy.
   * @private
   */
  private startSmartSilentRenew() {
    console.debug('[AuthenticationService] startSmartSilentRenew');
    interval(this.SMART_SILENT_RENEW_INTERVAL)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => this.userActivity.wasActiveInLastMinutes(this.SMART_SILENT_RENEW_INACTIVITY_THRESHOLD)),
        switchMap(() =>
          this.oidcSecurityService.forceRefreshSession(undefined, this.activeConfigId).pipe(
            catchError((err) => {
              console.error('[AuthenticationService] Smart silent renew failed:', err);
              this.appInsightService.trackEvent('SmartSilentTokenRefreshFailed', {
                Message: `[AuthenticationService][SmartSilentRenew] Smart silent renew failed: ${err.message}`,
                Frontend: 'true',
                Service: 'Auth',
              });
              return of(null);
            })
          )
        )
      )
      .subscribe((result) => {
        if (result) {
          console.debug('[AuthenticationService] ✅ Token successfully refreshed', result);
          this.appInsightService.trackEvent('SmartSilentTokenRefreshed', {
            Message: '[AuthenticationService][SmartSilentRenew] Token successfully refreshed',
            Frontend: 'true',
            Service: 'Auth',
          });
        }
      });
  }


  private redirectToUnauthorizedHtml() {
    // moved to an external method only to avoid redirect in tests
    window.location.href = '/unauthorized.html';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
